// KYC Region Configuration
// This file determines which regions require KYC verification by checking the database

import { API } from '../config/apiConfig';

// Cache for KYC requirements to avoid repeated API calls
const kycRequirementCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches KYC requirement for a country from the database
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {Promise<object>} - { required: boolean, reason: string, data: object }
 */
export async function fetchKYCRequirementFromDB(countryCode) {
  if (!countryCode) {
    return { 
      required: false, 
      reason: 'No country code provided',
      data: null
    };
  }

  const upperCountryCode = countryCode.toUpperCase();

  // Check cache first
  const cacheKey = upperCountryCode;
  const cached = kycRequirementCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`📦 Using cached KYC requirement for ${upperCountryCode}`);
    return cached.data;
  }

  try {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const response = await fetch(
      `${API.kycRequirementByCountry}?countryCode=${encodeURIComponent(upperCountryCode)}`,
      { headers }
    );

    if (!response.ok) {
      // If country not found (404), default to not required
      if (response.status === 404) {
        console.log(`ℹ️ Country ${upperCountryCode} not found in KYC requirements, defaulting to not required`);
        const result = {
          required: false,
          reason: 'KYC is not required for your country',
          data: null
        };
        kycRequirementCache.set(cacheKey, { data: result, timestamp: Date.now() });
        return result;
      }
      
      // For other errors, throw to be caught below
      throw new Error(`Failed to fetch KYC requirement: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success || !result.data) {
      console.log(`ℹ️ No KYC requirement data for ${upperCountryCode}, defaulting to not required`);
      const defaultResult = {
        required: false,
        reason: 'KYC is not required for your country',
        data: null
      };
      kycRequirementCache.set(cacheKey, { data: defaultResult, timestamp: Date.now() });
      return defaultResult;
    }

    const kycData = result.data;
    
    // Check if KYC is required based on database values
    // IsKYCRequired must be true AND IsActive must be true (or null/undefined)
    const isRequired = kycData.IsKYCRequired === true && kycData.IsActive !== false;
    
    const requirementResult = {
      required: isRequired,
      reason: isRequired 
        ? (kycData.RequirementLevel === 'Mandatory' 
            ? 'KYC is mandatory in your region for financial compliance'
            : 'KYC is required in your region')
        : 'KYC is not required for your country',
      data: kycData,
      requirementLevel: kycData.RequirementLevel,
      kycProvider: kycData.KYCProvider,
      complianceRegion: kycData.ComplianceRegion,
    };

    // Cache the result
    kycRequirementCache.set(cacheKey, { data: requirementResult, timestamp: Date.now() });
    
    console.log(`✅ Fetched KYC requirement for ${upperCountryCode}:`, requirementResult);
    return requirementResult;
  } catch (error) {
    console.error(`❌ Error fetching KYC requirement for ${upperCountryCode}:`, error);
    
    // On error, default to not required (safer for users)
    const errorResult = {
      required: false,
      reason: 'Unable to verify KYC requirements. KYC is optional.',
      data: null,
      error: error.message
    };
    
    // Cache error result for shorter duration (1 minute)
    kycRequirementCache.set(cacheKey, { data: errorResult, timestamp: Date.now() });
    return errorResult;
  }
}

/**
 * Determines if KYC verification is required for a given country code
 * This is the async version that checks the database
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {Promise<object>} - { required: boolean, reason: string, data: object }
 */
export async function isKYCRequired(countryCode) {
  return await fetchKYCRequirementFromDB(countryCode);
}

/**
 * Gets the user's country code from their profile or IP geolocation
 * @param {object} userProfile - User profile object
 * @returns {string|null} - Country code or null
 */
export function getUserCountryCode(userProfile) {
  // Try to get from user profile first (countryCode from JWT)
  if (userProfile?.countryCode) {
    return userProfile.countryCode;
  }

  // Try to get from user profile country field
  if (userProfile?.country) {
    return userProfile.country;
  }

  // Try to get from address
  if (userProfile?.address?.country) {
    return userProfile.address.country;
  }

  // Try to get from region
  if (userProfile?.region) {
    return userProfile.region;
  }

  // Fallback: return null
  return null;
}

/**
 * Determines if KYC should be shown in the UI for a user
 * This is the async version that checks the database
 * @param {object} userProfile - User profile object
 * @returns {Promise<object>} - { show: boolean, required: boolean, reason: string, countryCode: string }
 */
export async function shouldShowKYC(userProfile) {
  const countryCode = getUserCountryCode(userProfile);
  
  if (!countryCode) {
    console.log('⚠️ No country code found in user profile, defaulting to KYC not required');
    return {
      show: false,
      required: false,
      reason: 'Country information not available. KYC is optional.',
      countryCode: null
    };
  }

  const kycStatus = await isKYCRequired(countryCode);

  return {
    show: kycStatus.required,
    required: kycStatus.required,
    reason: kycStatus.reason,
    countryCode: countryCode,
    requirementLevel: kycStatus.requirementLevel,
    kycProvider: kycStatus.kycProvider,
    complianceRegion: kycStatus.complianceRegion,
  };
}

/**
 * Gets a user-friendly message about KYC requirements
 * @param {object} userProfile - User profile object
 * @returns {Promise<string>} - User-friendly message
 */
export async function getKYCMessage(userProfile) {
  const kycInfo = await shouldShowKYC(userProfile);
  
  if (kycInfo.required) {
    return "Identity verification is required in your region to access all features.";
  }
  
  return "Identity verification is optional in your region.";
}

/**
 * Normalizes the KYC status payload returned by the API so UI components
 * can rely on consistent fields (verified boolean, status string, etc.)
 * @param {object} rawStatus
 * @returns {object}
 */
export function normalizeKYCStatus(rawStatus) {
  const fallback = {
    verified: false,
    status: 'not_started',
  };

  if (!rawStatus) {
    return fallback;
  }

  const extractRecord = (input) => {
    if (!input || typeof input !== 'object') return null;

    // Direct match
    if ('verified' in input || 'status' in input || 'state' in input) {
      return input;
    }

    // Scan children (objects / arrays)
    if (Array.isArray(input)) {
      for (const item of input) {
        const match = extractRecord(item);
        if (match) return match;
      }
    } else {
      for (const key of Object.keys(input)) {
        const match = extractRecord(input[key]);
        if (match) return match;
      }
    }

    return null;
  };

  const payload = extractRecord(rawStatus.data ?? rawStatus) ?? fallback;

  const status = (payload.status || payload.state || '').toLowerCase();
  const verifiedFlag =
    payload.verified === true ||
    status === 'verified' ||
    status === 'complete' ||
    status === 'completed' ||
    status === 'approved';

  return {
    ...payload,
    verified: verifiedFlag,
    status: status || (verifiedFlag ? 'verified' : 'not_started'),
  };
}

/**
 * Clears the KYC requirement cache (useful for testing or after admin updates)
 */
export function clearKYCCache() {
  kycRequirementCache.clear();
  console.log('🗑️ KYC requirement cache cleared');
}
